import React from 'react'
import { IconButton, InputAdornment, TextField, Tooltip, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import Tag from 'components/Tag'
import FocusFadeGroup from 'components/FocusFadeGroup'
import { cleanName } from 'services/text'
import { CheckCircle, Create } from '@mui/icons-material'
import { graphql, useFragment } from 'react-relay'
import { useQuickCommit } from 'hooks/useQuickCommit'
import { NameFields_User$key } from '__generated__/NameFields_User.graphql'
import { NameFields_UpdateUserMutation } from '__generated__/NameFields_UpdateUserMutation.graphql'
import useStyles from './styles'

const PLACEHOLDER = 'Your Full Name'

interface INameFieldsProps {
  user: NameFields_User$key
  editable?: boolean
}

function NameFields(props: INameFieldsProps) {
  const { classes } = useStyles()
  const theme = useTheme()
  const { editable, user: userFragmentRef } = props

  const user = useFragment(graphql`
    fragment NameFields_User on User {
      firstName
      lastName
    }
  `, userFragmentRef)

  const [isEditMode, setEditMode] = React.useState(false)
  const [fullName, setFullName] = React.useState(`${user.firstName || ''} ${user.lastName || ''}`.trim())
  const [textFieldOriginWidth, setTextFieldOriginWidth] = React.useState(0)
  const [saveError, setSaveError] = React.useState<string>()

  const validationError = (!user?.firstName || !user?.lastName) && editable ? 'Required' : null
  const error = saveError || validationError

  const nameWrapperRef = React.useRef<HTMLSpanElement>(null)
  const textFieldRef = React.useRef<HTMLDivElement>(null)

  const { execute: updateUser } = useQuickCommit<NameFields_UpdateUserMutation>(graphql`
    mutation NameFields_UpdateUserMutation($input: UpdateUserInput!) {
      updateUser(input: $input) {
        user {
          firstName
          lastName
        }
      }
    }
  `)

  React.useEffect(() => {
    let newFullName = `${user.firstName || ''} ${user.lastName || ''}`.trim()

    if (fullName !== newFullName && !isEditMode) {
      setFullName(newFullName)
    }
  }, [user.firstName, user.lastName, fullName, isEditMode])

  React.useEffect(() => {
    if (isEditMode && textFieldRef?.current) {
      let textFieldElement = textFieldRef?.current

      if (textFieldElement.animate) {
        textFieldElement.animate(
          [
            { maxWidth: `${(textFieldOriginWidth + 6) || 0}px` },
            { maxWidth: `${textFieldElement.clientWidth || 0}px` },
          ],
          {
            duration: 450,
            easing: 'cubic-bezier(0.6, 0, 0, 1)',
          },
        )
      }
    }
  }, [isEditMode, textFieldOriginWidth])

  const handleEditClick = () => {
    if (!editable) return

    setEditMode(true)
    setTextFieldOriginWidth(nameWrapperRef.current?.clientWidth || textFieldOriginWidth)
  }

  const handleBlur = async () => {
    const fullNameCleaned = cleanName(fullName)
    const delimiterIndex = fullNameCleaned.indexOf(' ')
    const nameParts = delimiterIndex === -1 ? [fullNameCleaned] : [fullNameCleaned.substring(0, delimiterIndex), fullNameCleaned.substring(delimiterIndex + 1)]

    const firstNameValue = nameParts[0] || ''
    const lastNameValue = nameParts[1] || ''

    const firstNameChanged = (user.firstName || '') !== firstNameValue
    const lastNameChanged = (user.lastName || '') !== lastNameValue

    if (firstNameChanged || lastNameChanged) {
      try {
        const result = await updateUser({
          input: {
            firstName: firstNameValue,
            lastName: lastNameValue,
          },
        })

        if (result?.updateUser?.user) {
          setSaveError(undefined)
        } else {
          setSaveError('Failed to update name')
        }
      } catch (error: any) {
        setSaveError(error?.message || 'Failed to update name')
        return
      }
    }

    setEditMode(false)
    setFullName(`${firstNameValue} ${lastNameValue}`.trim())
  }

  const handleFullNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFullName(e.target.value)
  }

  return (
    <Tag
      className={classes.container}
      onClick={handleEditClick}
      style={{
        margin: isEditMode ? theme.spacing(2, 0) : undefined,
      }}
    >
      <FocusFadeGroup focused={isEditMode} style={{ display: 'inline-block' }}>
        {isEditMode && (
          <Typography variant="h3">
            <TextField
              ref={textFieldRef}
              autoFocus
              value={fullName}
              placeholder={PLACEHOLDER}
              multiline
              maxRows={4}
              onChange={handleFullNameChange}
              onBlur={handleBlur}
              className={classes.field}
              inputProps={{
                style: { fontSize: 'inherit' },
                'data-cy': 'profile-name-field',
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Close" placement="top">
                      <IconButton data-cy="profile-name-save" size="large" onClick={handleBlur}>
                        <CheckCircle />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
          </Typography>
        )}

        {!isEditMode && (
          <Typography variant="h3" component="span" className={classes.name} style={fullName ? undefined : { opacity: 0.5 }} ref={nameWrapperRef}>
            {fullName || PLACEHOLDER}
          </Typography>
        )}

        {error && (
          <Typography variant="body1" className={classes.error} data-cy="profile-name-error">
            {error}
          </Typography>
        )}

        {editable && !isEditMode && (
          <Tooltip title="Edit name" placement="top">
            <IconButton data-cy="profile-name-edit" className={classes.edit} size="large">
              <Create />
            </IconButton>
          </Tooltip>
        )}
      </FocusFadeGroup>
    </Tag>
  )
}

export default React.memo(NameFields)
